import { useRef, useState } from "react";
import { Form, Input, Select, Row, Col, Spin, Tooltip, Button, Checkbox, Typography } from "antd";
import Layout from "../../../Containers/Layout";
import { Redirect, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getSubject, updateSubject, addSubject } from "../../../Requests/subject-requests";
import ImportSubjectModal from "./ImportSubjectModal";
import { NOTIFICATION_TYPES, openNotification } from "../../Notifications/NotificationsUtils";
import { getUserId, useIsAdmin } from "../../../utils/utilFunctions";
import ProgramCompetenceForm, { getAutoIdentifier } from "../../StudyPrograms/StudyProgramForm/ProgramCompetenceForm";
import { theme } from "../../../theme";
import ConfirmationModal from "../../../Containers/ConfirmationModal";
import CustomEditor from "../../../CustomComponents/CustomEditor";
import SubjectContentForm from "./SubjectCursContentForm";
import SubjectEvaluationForm from "./SubjectLaboratoryContentForm";
import { AcademicUser, AddSubjectFileDTO, EvaluationTypeEnum, GradingTypeEnum, IndicatorTypeEnum, Laboratory, LearningCompetenceTypeEnum, SemesterTypeEnum, StudyCycleEnum, StudyPlanDTO, StudyProgram, StudyProgramLangEnum, StudyYearEnum, SubjectContent, SubjectEvaluation, SubjectLearningOutcome, TeachingTypeEnum, SubjectStatusEnum } from "../../../Api";
import styles from "./SubjectForm.module.scss";
import { getStudyProgram } from "../../../Requests/study-program-request";
import useDebounce from "../../../Hooks/debounce";
import { getAllAcademicUsers } from "../../../Requests/academic-user-requests";
import { getDepartmentsForFaculty } from "../../../Requests/faculty-requests";
import { mapStudyProgramLangToString, parseSubjectStatusValidationErrors, useBlockedByStatus } from "../helpers";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DisciplineCategoryEnum } from "../../../Api";
import { InputNumber } from "antd";

const { Option } = Select;

const MIN_ADDED_OUTCOMES = 3;
const MAX_ADDED_OUTCOMES = 15;

const buildLearningOutcomesPayload = (values: any): SubjectLearningOutcome[] => {
  const mapType = (list: any[] | undefined, type: LearningCompetenceTypeEnum) =>
    (list ?? [])
      .filter((outcome: any) => outcome && !outcome?.isLocked && (outcome.description ?? '').trim() !== '')
      .map((outcome: any) => ({ id: outcome.id || undefined, description: outcome.description, type }));

  return [
    ...mapType(values.fisaKnowledgeOutcomeValues, LearningCompetenceTypeEnum.Knowledge),
    ...mapType(values.fisaSkillOutcomeValues, LearningCompetenceTypeEnum.Skill),
    ...mapType(values.fisaResponsibilityOutcomeValues, LearningCompetenceTypeEnum.ResponsibilityAutonomy),
  ];
};

const SubjectForm = (props: any) => {
  const { t } = useTranslation();
  const currentUserId = getUserId();
  const { subjectId } = useParams<{ subjectId: string }>();
  const [redirect, setRedirect] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const { programId, studyCycle } = useParams<{ programId: string, studyCycle: StudyCycleEnum }>();
  const isDoctorate = studyCycle === StudyCycleEnum.Doctorate;

  const location = useLocation();

  const locationState = location.state as { plan?: StudyPlanDTO; from?: string } | undefined;

  // Titularii unei discipline existente se aloca doar din planul de invatamant (butonul "Asignare titulari",
  // SubjectController.AssignCoordinators); backend-ul ignora oricum titularii trimisi la salvarea fisei.
  const coordinatorsReadOnly = !!subjectId;
  const coordinatorsHint = coordinatorsReadOnly
    ? 'Titularii se modifică din planul de învățământ, prin butonul „Asignare titulari".'
    : undefined;

  const disabledByStatus = useBlockedByStatus(subjectId);

  // Guards the big form.setFieldsValue(...) population below so it only runs on the
  // first successful load of this subject, not on any later refetch of the same
  // ["getSubject", subjectId] query key while the form stays mounted. Without this,
  // a background refetch would silently discard any unsaved edits the titular made
  // on the fișă. Other consumers of this same query key (useIsTitular, useIsLabTitular,
  // useSubject) are unaffected — they read fresh `data`/`subject` on every fetch
  // regardless of this ref.
  const formInitialized = useRef(false);

  const [searchTermNameCourse, setCourseUserSearchTerm] = useState('');
	const debouncedCourseUserSearch = useDebounce(searchTermNameCourse, 1000);

  const [searchTermNameLab, setLabUserSearchTerm] = useState('');
	const debouncedLabUserSearch = useDebounce(searchTermNameLab, 1000);
  
  const isAdmin = useIsAdmin();
  
  const [form] = Form.useForm<{
    nameRO: string | null;
    nameEN: string | null;
    departmentId: string | null;
    semester: SemesterTypeEnum;
    studyYear: StudyYearEnum;
    schoolYear: number;
    studyProgramId: string; 
    evaluationType: EvaluationTypeEnum;
    subjectType: IndicatorTypeEnum;
    subjectCategory: IndicatorTypeEnum;
    code: string | null;
    gradingType: GradingTypeEnum;
    totalWeeklyHours: number;
    courseWeeklyHours: number;
    labWeeklyHours: number;
    projectWeeklyHours: number;
    seminarWeeklyHours: number;
    selfStudyHours: number;
    tutoringHours: number;
    otherActivitiesHours: number;
    totalHours?: number; 
    courseHours?: number;
    labHours?: number;
    totalSelfStudyHours?: number;
    totalSemesterHours?: number;
    credits?: number;
    creditsIIS?: number;
    creditsOE?: number;
    labAtPartner?: boolean;
    seminarAtPartner?: boolean;
    projectAtPartner?: boolean;
    practiceAtPartner?: boolean;
    curriculumPrecondition: string | null;
    respultsPrecondition: string | null;
    courseConditions: string | null;
    labConditions: string | null;
    generalObjective: string | null;
    specificCompetence: string | null;
    generalCompetence: string | null;
    teachingKnowledges: string | null;
    teachingAptitudes: string | null;
    teachingResponsabilities: string | null;
    teachingMethods: string | null;
    courseBibliography: string | null;
    labBibliography: string | null;
    promotionConditions: string | null;
    minimumPromotionOutcomeIds: string[] | null;
    subjectCoroboration: string | null;
    studyProgram: StudyProgram; 
    courseCoordinatorIds: string[] | null;
    labCoordinatorIds: string[] | null;
    laboratories: Laboratory[] | null;
    courseContents: SubjectContent[];
    labContents: SubjectContent[]; 
    courseEvaluations: SubjectEvaluation[];
    labEvaluations: SubjectEvaluation[];
    fisaKnowledgeOutcomeValues: any[];
    fisaSkillOutcomeValues: any[];
    fisaResponsibilityOutcomeValues: any[];
    disciplineCategory: DisciplineCategoryEnum;
    practiceTotalHours: number;  
  }>();

  const category = Form.useWatch('disciplineCategory', form);

  const watchedKnowledgeOutcomes = Form.useWatch('fisaKnowledgeOutcomeValues', form);
  const watchedSkillOutcomes = Form.useWatch('fisaSkillOutcomeValues', form);
  const watchedResponsibilityOutcomes = Form.useWatch('fisaResponsibilityOutcomeValues', form);
  const addedOutcomesCount = [watchedKnowledgeOutcomes, watchedSkillOutcomes, watchedResponsibilityOutcomes]
    .flatMap(list => list ?? [])
    .filter((outcome: any) => !outcome?.isLocked).length;

  const buildMinimumOutcomeOptions = (list: any[] | undefined, prefix: string) =>
    (list ?? [])
      .map((outcome: any, index: number) => ({
        id: outcome?.id as string | undefined,
        key: `${prefix}-${index}`,
        description: (outcome?.description ?? '').trim(),
        label: `${getAutoIdentifier(prefix, index)}: ${outcome?.description ?? ''}`,
      }))
      .filter(option => !!option.description);

  const minimumOutcomeOptions = [
    ...buildMinimumOutcomeOptions(watchedKnowledgeOutcomes, 'C'),
    ...buildMinimumOutcomeOptions(watchedSkillOutcomes, 'A'),
    ...buildMinimumOutcomeOptions(watchedResponsibilityOutcomes, 'RA'),
  ];

  const handleOutcomeSelectionInvalidated = (id: string) => {
    form.setFieldsValue({
      minimumPromotionOutcomeIds: (form.getFieldValue('minimumPromotionOutcomeIds') ?? []).filter((x: string) => x !== id),
    });
  };

  const openGetErrorNotification = (_error: any) => {
    openNotification(
      'Eroare',
      'Nu s-au putut prelua datele de pe server!',
      NOTIFICATION_TYPES.ERROR
    );
  };

  const { data: courseUsers, isLoading: isLoadingCourseSearch } = useQuery(['getAcademiUsersCourse', debouncedCourseUserSearch],
    () => {
        const response = getAllAcademicUsers(debouncedCourseUserSearch, 1, 10, undefined);
        return response;
    },
    {
        onError: openGetErrorNotification,
    }
  );

  const { data: labUsers, isLoading: isLoadingLabSearch } = useQuery(['getAcademiUsersLab', debouncedLabUserSearch],
    () => {
        const response = getAllAcademicUsers(debouncedLabUserSearch, 1, 10, undefined);
        return response;
    },
    {
        onError: openGetErrorNotification,
    }
  );

  

  const { data: program } = useQuery( //TODO error handling
    ["getProgram", programId], 
    () => getStudyProgram(programId)
  );
  const isDualProgramPlan = !!program?.isDualProgram;
  
  const { data: departments } = useQuery(
    ['getDepartments'],
    () => {
      const faculties = program?.facultyId ? [program.facultyId] : [];
      return getDepartmentsForFaculty(faculties);
    },
    {
      enabled: !!program?.facultyId,
      onError: openGetErrorNotification,
    }
  );

  const { data: subject } = useQuery(["getSubject", subjectId], () => getSubject(subjectId), {
    onError: (err) => {
      openGetErrorNotification(err);
    },
    onSuccess: (data) => {
      // Only populate the form from server data on the first successful load. A later
      // refetch of this query key must not re-run this, or any unsaved edits elsewhere
      // on the fișă would be silently overwritten with stale server state.
      if (data && !formInitialized.current) {
        const hasSplitCredits = (Number(data.creditsIIS ?? 0) + Number(data.creditsOE ?? 0)) > 0;
        const normalizedCreditsIIS = hasSplitCredits
          ? Number(data.creditsIIS ?? 0)
          : Number(data.credits ?? 0);
        const normalizedCreditsOE = hasSplitCredits ? Number(data.creditsOE ?? 0) : 0;

        const courseContents = data.subjectContents?.filter(content => content.teachingType === TeachingTypeEnum.Course);
        const labContents = data.subjectContents?.filter(content => content.teachingType === TeachingTypeEnum.Lab);

        const courseEvaluations = data.subjectEvaluations?.filter(content => content.teachingType === TeachingTypeEnum.Course);
        const labEvaluations = data.subjectEvaluations?.filter(content => content.teachingType === TeachingTypeEnum.Lab);

        const mapFisaOutcomes = (type: LearningCompetenceTypeEnum) => (data.fisaLearningOutcomes ?? [])
          .filter(outcome => outcome.type === type)
          .map(outcome => ({ id: outcome.sourceId, description: outcome.description, isLocked: outcome.isLocked }));

        const fisaKnowledgeOutcomeValues = mapFisaOutcomes(LearningCompetenceTypeEnum.Knowledge);
        const fisaSkillOutcomeValues = mapFisaOutcomes(LearningCompetenceTypeEnum.Skill);
        const fisaResponsibilityOutcomeValues = mapFisaOutcomes(LearningCompetenceTypeEnum.ResponsibilityAutonomy);

        form.setFieldsValue({
          nameRO: data.nameRO,
          nameEN: data.nameEN,
          departmentId: data?.departmentId,
          semester: data.semester,
          studyYear: isDoctorate ? StudyYearEnum.D1 : data.studyYear,
          schoolYear: data.schoolYear,
          studyProgramId: data.studyProgramId,
          evaluationType: isDoctorate ? EvaluationTypeEnum.V : data.evaluationType,
          subjectType: data.subjectType,
          subjectCategory: data.subjectCategory,
          code: data.code,
          gradingType: data.gradingType,
          totalWeeklyHours: data.totalWeeklyHours,
          courseWeeklyHours: data.courseWeeklyHours,
          labWeeklyHours: data.labWeeklyHours,
          projectWeeklyHours: data.projectWeeklyHours,
          seminarWeeklyHours: data.seminarWeeklyHours,
          totalHours: data.totalHours,
          courseHours: data.courseHours,
          labHours: data.labHours,
          selfStudyHours: data.selfStudyHours,
          tutoringHours: data.tutoringHours,
          otherActivitiesHours: data.otherActivitiesHours,
          totalSelfStudyHours: data.totalSelfStudyHours,
          totalSemesterHours: data.totalSemesterHours,
          credits: data.credits,
          creditsIIS: normalizedCreditsIIS,
          creditsOE: normalizedCreditsOE,
          labAtPartner: data.labAtPartner ?? false,
          seminarAtPartner: data.seminarAtPartner ?? false,
          projectAtPartner: data.projectAtPartner ?? false,
          practiceAtPartner: data.practiceAtPartner ?? false,
          curriculumPrecondition: data.curriculumPrecondition,
          respultsPrecondition: data.respultsPrecondition,
          courseConditions: data.courseConditions,
          labConditions: data.labConditions,
          generalObjective: data.generalObjective,
          specificCompetence: data.specificCompetence,
          generalCompetence: data.generalCompetence,
          teachingKnowledges: data.teachingKnowledges,
          teachingAptitudes: data.teachingAptitudes,
          teachingResponsabilities: data.teachingResponsabilities,
          teachingMethods: data.teachingMethods,
          courseBibliography: data.courseBibliography,
          labBibliography: data.labBibliography,
          promotionConditions: data.promotionConditions,
          minimumPromotionOutcomeIds: (data.minimumPromotionOutcomes ?? []).map(outcome => outcome.sourceId).filter((id): id is string => !!id),
          subjectCoroboration: data.subjectCoroboration,
          studyProgram: data.studyProgram,
          courseCoordinatorIds: data.courseCoordinators?.map((user: AcademicUser) => user.id || '') || [],
          labCoordinatorIds: data.labCoordinators?.map((user: AcademicUser) => user.id || '') || [],
          laboratories: data.laboratories,
          courseContents,
          labContents,
          courseEvaluations,
          labEvaluations,
          fisaKnowledgeOutcomeValues,
          fisaSkillOutcomeValues,
          fisaResponsibilityOutcomeValues,
          disciplineCategory: isDoctorate ? DisciplineCategoryEnum.PracticeStage : (data.disciplineCategory ?? DisciplineCategoryEnum.Standard),
          practiceTotalHours: data.practiceTotalHours ?? 0,
        });

        form.setFieldsValue({
          credits: normalizedCreditsIIS + normalizedCreditsOE || Number(data.credits) || 0,
          totalSemesterHours: (normalizedCreditsIIS + normalizedCreditsOE || Number(data.credits) || 0) * 25
        });

        formInitialized.current = true;
      }
    },
    enabled: !!subjectId,
    refetchOnWindowFocus: false
  });

  //TODO cleanup error and success message functions
  const openSaveErrorNotification = async (error: any) => {
    const validationErrors = await parseSubjectStatusValidationErrors(error);
    if (validationErrors) {
      openNotification(
        'Eroare!',
        <div>{validationErrors.map(message => <div key={message}>• {message}</div>)}</div>,
        NOTIFICATION_TYPES.ERROR,
        0
      );
      return;
    }

    openNotification(
      'Eroare!',
      'Nu s-a putut salva disciplina!',
      NOTIFICATION_TYPES.ERROR
    );
  };

  const handleCancel = () => {
    setRedirect(true);
  };

  const openSuccessNotificationAndRedirect = () => {
    setRedirect(true);
    openNotification(
      'Succes!',
      'Disciplina a fost salvată cu succes!',
      NOTIFICATION_TYPES.SUCCESS,
      6
    );
  };

  const handleImportSubject = async (subjectId: string) => {
    try {
      const importedSubject = await getSubject(subjectId);
      if (importedSubject) {
        const courseContents = importedSubject.subjectContents?.filter(content => content.teachingType === TeachingTypeEnum.Course) || [];
        const labContents = importedSubject.subjectContents?.filter(content => content.teachingType === TeachingTypeEnum.Lab) || [];

        const courseEvaluations = importedSubject.subjectEvaluations?.filter(content => content.teachingType === TeachingTypeEnum.Course) || [];
        const labEvaluations = importedSubject.subjectEvaluations?.filter(content => content.teachingType === TeachingTypeEnum.Lab) || [];

        
        form.setFieldsValue({
          curriculumPrecondition: importedSubject.curriculumPrecondition,
          respultsPrecondition: importedSubject.respultsPrecondition,
          courseConditions: importedSubject.courseConditions,
          labConditions: importedSubject.labConditions,
          generalObjective: importedSubject.generalObjective,
          specificCompetence: importedSubject.specificCompetence,
          generalCompetence: importedSubject.generalCompetence,
          teachingMethods: importedSubject.teachingMethods,
          courseBibliography: importedSubject.courseBibliography,
          labBibliography: importedSubject.labBibliography,
          promotionConditions: importedSubject.promotionConditions,
          subjectCoroboration: importedSubject.subjectCoroboration,
          // Importul aduce doar continut de fisa. Nu are voie sa atinga datele administrative ale
          // disciplinei editate: titularii (a fost incident), programul de studii (sectiunea 1) sau
          // categoria disciplinei si orele de practica (sectiunile 2-3, schimba regimul si pune pe
          // zero orele la salvare).
          laboratories: importedSubject.laboratories,
          courseContents,
          labContents,
          courseEvaluations,
          labEvaluations,
        });

        openNotification(
          'Succes!',
          'Datele disciplinei au fost importate cu succes!',
          NOTIFICATION_TYPES.SUCCESS
        );
      }
    } catch (error) {
      openNotification(
        'Eroare!',
        'Nu s-au putut importa datele disciplinei!',
        NOTIFICATION_TYPES.ERROR
      );
    }
  };

  const handleSave = async () => {
  let values;
  try {
    values = await form.validateFields();
  } catch (validationError) {
    openNotification(
      'Eroare!',
      'Exista erori de validare, verificati valorile introduse',
      NOTIFICATION_TYPES.ERROR
    );
    return;
  }

  if (values.nameRO && values.studyYear) {

    // === noi: sar peste validările pe ore când e S’ (PracticeStage)
    const isPractice = values.disciplineCategory === DisciplineCategoryEnum.PracticeStage;

    if (!isPractice) {
      const totalCourseContentHours = values.courseContents.reduce(
        (total, content) => total + (Number(content.hoursNumber) || 0),
        0
      );
      const totalLabContentHours = values.labContents.reduce(
        (total, content) => total + (Number(content.hoursNumber) || 0),
        0
      );

      if (totalCourseContentHours !== values.courseHours) {
        openNotification(
          'Eroare!',
          `Numărul total de ore din conținutul cursului (${totalCourseContentHours}) trebuie să fie egal cu numărul de ore alocate cursului (${values.courseHours}).`,
          NOTIFICATION_TYPES.ERROR
        );
        return;
      }

      if (totalLabContentHours !== values.labHours) {
        openNotification(
          'Eroare!',
          `Numărul total de ore din conținutul laboratorului (${totalLabContentHours}) trebuie să fie egal cu numărul de ore alocate laboratorului (${values.labHours}).`,
          NOTIFICATION_TYPES.ERROR
        );
        return;
      }

      const calculatedSelfStudyHours = 
        (Number(values.selfStudyHours) || 0) +
        (Number(values.tutoringHours) || 0) +
        (Number(values.otherActivitiesHours) || 0);

      if (calculatedSelfStudyHours !== values.totalSelfStudyHours) {
        openNotification(
          'Eroare!',
          `Suma orelor de studiu individual (${calculatedSelfStudyHours}) trebuie să fie egală cu totalul de ore de studiu individual (${values.totalSelfStudyHours}).`,
          NOTIFICATION_TYPES.ERROR
        );
        return;
      }
    }

    const subjectData: AddSubjectFileDTO = {
      nameRO: values.nameRO,
      nameEN: values.nameEN,
      departmentId: values.departmentId || '00000000-0000-0000-0000-000000000000',
      semester: values.semester,
      studyYear: isDoctorate ? StudyYearEnum.D1 : values.studyYear,
      studyProgramId: program?.id,
      evaluationType: isDoctorate ? EvaluationTypeEnum.V : values.evaluationType,
      subjectType: values.subjectType,
      subjectCategory: values.subjectCategory,
      code: values.code,
      gradingType: values.gradingType,

      disciplineCategory: isDoctorate ? DisciplineCategoryEnum.PracticeStage : values.disciplineCategory,
      practiceTotalHours: values.practiceTotalHours ?? 0,

      totalWeeklyHours: isPractice ? 0 : values.totalWeeklyHours,
      courseWeeklyHours: isPractice ? 0 : values.courseWeeklyHours,
      labWeeklyHours: isPractice ? 0 : values.labWeeklyHours,
      projectWeeklyHours: isPractice ? 0 : values.projectWeeklyHours,
      seminarWeeklyHours: isPractice ? 0 : values.seminarWeeklyHours,

      totalHours: values.totalHours,
      courseHours: values.courseHours,
      labHours: values.labHours,
      selfStudyHours: values.selfStudyHours,
      tutoringHours: values.tutoringHours,
      examHours: 0,
      otherActivitiesHours: values.otherActivitiesHours,
      totalSelfStudyHours: values.totalSelfStudyHours,
      totalSemesterHours: values.totalSemesterHours,
      credits: isDualProgramPlan
        ? (Number(values.creditsIIS) || 0) + (Number(values.creditsOE) || 0)
        : (Number(values.credits) || 0),
      creditsIIS: values.creditsIIS,
      creditsOE: values.creditsOE,
      labAtPartner: !!values.labAtPartner,
      seminarAtPartner: !!values.seminarAtPartner,
      projectAtPartner: !!values.projectAtPartner,
      practiceAtPartner: !!values.practiceAtPartner,

      curriculumPrecondition: values.curriculumPrecondition,
      respultsPrecondition: values.respultsPrecondition,
      courseConditions: values.courseConditions,
      labConditions: values.labConditions,
      generalObjective: values.generalObjective,
      specificCompetence: values.specificCompetence,
      generalCompetence: values.generalCompetence,
      teachingKnowledges: values.teachingKnowledges,
      teachingAptitudes: values.teachingAptitudes,
      teachingResponsabilities: values.teachingResponsabilities,
      teachingMethods: values.teachingMethods,
      learningOutcomes: buildLearningOutcomesPayload(values),

      subjectContents: [
        ...(values.courseContents.map(e => ({ ...e, teachingType: TeachingTypeEnum.Course }))),
        ...(values.labContents.map(e => ({ ...e, teachingType: TeachingTypeEnum.Lab })))
      ],

      courseBibliography: values.courseBibliography,
      labBibliography: values.labBibliography,

      subjectEvaluations: [
        ...(values.courseEvaluations.map(e => ({ ...e, teachingType: TeachingTypeEnum.Course }))),
        ...(values.labEvaluations.map(e => ({ ...e, teachingType: TeachingTypeEnum.Lab })))
      ],

      promotionConditions: values.promotionConditions,
      minimumPromotionOutcomeIds: values.minimumPromotionOutcomeIds ?? [],
      subjectCoroboration: values.subjectCoroboration,
      // la editare nu se trimit (undefined = neatins pe server)
      courseCoordinatorIds: coordinatorsReadOnly ? undefined : values.courseCoordinatorIds,
      labCoordinatorIds: coordinatorsReadOnly ? undefined : values.labCoordinatorIds,
      isPartiallySaved: false,
    };

    const saveAction = subjectId ? updateSubject(subjectId, subjectData) : addSubject(subjectData);
    saveAction.then(openSuccessNotificationAndRedirect).catch(openSaveErrorNotification);
  } else {
    openNotification(
      'Eroare!',
      'Vă rugăm să completați toate câmpurile obligatorii!',
      NOTIFICATION_TYPES.ERROR
    );
  }
};

  const handlePartialSave = () => {
    // partial save only allowed for draft sheets
    const status = subject?.status ?? SubjectStatusEnum.Draft;
    if (status !== SubjectStatusEnum.Draft) {
      openNotification(
        'Eroare!',
        'Salvarea parțială este permisă doar pentru fișe în starea Draft.',
        NOTIFICATION_TYPES.ERROR
      );
      return;
    }

    const values = form.getFieldsValue(true) as any;
    const isPractice = values.disciplineCategory === DisciplineCategoryEnum.PracticeStage;

    const courseContents = (values.courseContents ?? []).map((e: any) => ({ ...e, teachingType: TeachingTypeEnum.Course }));
    const labContents = (values.labContents ?? []).map((e: any) => ({ ...e, teachingType: TeachingTypeEnum.Lab }));
    const courseEvaluations = (values.courseEvaluations ?? []).map((e: any) => ({ ...e, teachingType: TeachingTypeEnum.Course }));
    const labEvaluations = (values.labEvaluations ?? []).map((e: any) => ({ ...e, teachingType: TeachingTypeEnum.Lab }));

    const subjectData: AddSubjectFileDTO = {
      nameRO: values.nameRO,
      nameEN: values.nameEN,
      departmentId: values.departmentId || '00000000-0000-0000-0000-000000000000',
      semester: values.semester,
      studyYear: values.studyYear,
      studyProgramId: program?.id,
      evaluationType: values.evaluationType,
      subjectType: values.subjectType,
      subjectCategory: values.subjectCategory,
      code: values.code,
      gradingType: values.gradingType,

      disciplineCategory: values.disciplineCategory,
      practiceTotalHours: values.practiceTotalHours ?? 0,

      totalWeeklyHours: isPractice ? 0 : values.totalWeeklyHours,
      courseWeeklyHours: isPractice ? 0 : values.courseWeeklyHours,
      labWeeklyHours: isPractice ? 0 : values.labWeeklyHours,
      projectWeeklyHours: isPractice ? 0 : values.projectWeeklyHours,
      seminarWeeklyHours: isPractice ? 0 : values.seminarWeeklyHours,

      totalHours: values.totalHours,
      courseHours: values.courseHours,
      labHours: values.labHours,
      selfStudyHours: values.selfStudyHours,
      tutoringHours: values.tutoringHours,
      examHours: 0,
      otherActivitiesHours: values.otherActivitiesHours,
      totalSelfStudyHours: values.totalSelfStudyHours,
      totalSemesterHours: values.totalSemesterHours,
      credits: isDualProgramPlan
        ? (Number(values.creditsIIS) || 0) + (Number(values.creditsOE) || 0)
        : (Number(values.credits) || 0),
      creditsIIS: values.creditsIIS,
      creditsOE: values.creditsOE,
      labAtPartner: !!values.labAtPartner,
      seminarAtPartner: !!values.seminarAtPartner,
      projectAtPartner: !!values.projectAtPartner,
      practiceAtPartner: !!values.practiceAtPartner,

      curriculumPrecondition: values.curriculumPrecondition,
      respultsPrecondition: values.respultsPrecondition,
      courseConditions: values.courseConditions,
      labConditions: values.labConditions,
      generalObjective: values.generalObjective,
      specificCompetence: values.specificCompetence,
      generalCompetence: values.generalCompetence,
      teachingKnowledges: values.teachingKnowledges,
      teachingAptitudes: values.teachingAptitudes,
      teachingResponsabilities: values.teachingResponsabilities,
      teachingMethods: values.teachingMethods,
      learningOutcomes: buildLearningOutcomesPayload(values),

      subjectContents: [...courseContents, ...labContents],
      courseBibliography: values.courseBibliography,
      labBibliography: values.labBibliography,

      subjectEvaluations: [...courseEvaluations, ...labEvaluations],

      promotionConditions: values.promotionConditions,
      minimumPromotionOutcomeIds: values.minimumPromotionOutcomeIds ?? [],
      subjectCoroboration: values.subjectCoroboration,
      // la editare nu se trimit (undefined = neatins pe server)
      courseCoordinatorIds: coordinatorsReadOnly ? undefined : values.courseCoordinatorIds,
      labCoordinatorIds: coordinatorsReadOnly ? undefined : values.labCoordinatorIds,

      isPartiallySaved: true,
    };

    const saveAction = subjectId ? updateSubject(subjectId, subjectData) : addSubject(subjectData);
    saveAction.then(openSuccessNotificationAndRedirect).catch(openSaveErrorNotification);
  };

  
  if (redirect) {
    return <Redirect to={{ pathname: locationState?.from ?? ('/' + studyCycle + '/plan/' + program?.id) }}></Redirect>;
  }
  else {
    return (
    <Layout>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setIsImportModalVisible(true)}
            style={{ background: theme.secondColor, color: theme.white, marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px', borderRadius: '10px' }}
          >
            Importă
          </Button>
        </div>
        <Form 
          form={form} 
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            if (
              changedValues.labWeeklyHours !== undefined ||
              changedValues.seminarWeeklyHours !== undefined ||
              changedValues.projectWeeklyHours !== undefined
            ) {
              const labWeekly = Number(allValues.labWeeklyHours) || 0;
              const seminarWeekly = Number(allValues.seminarWeeklyHours) || 0;
              const projectWeekly = Number(allValues.projectWeeklyHours) || 0;
              const calculatedLabHours = 14 * (labWeekly + seminarWeekly + projectWeekly);
              form.setFieldsValue({ labHours: calculatedLabHours });
            }

            if (changedValues.credits !== undefined) {
              const credits = Number(allValues.credits) || 0;
              form.setFieldsValue({ totalSemesterHours: credits * 25 });
            }
            if (changedValues.creditsIIS !== undefined || changedValues.creditsOE !== undefined) {
              const credits = (Number(allValues.creditsIIS) || 0) + (Number(allValues.creditsOE) || 0);
              form.setFieldsValue({ credits, totalSemesterHours: credits * 25 });
            }
          }}
        >
           <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex' }}>1. Date despre program</span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.1 Instituția de învățământ superior</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Universitatea Națională de Știință și Tehnologie POLITEHNICA București</span>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.2 Facultatea</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{program?.faculty?.name}</span>
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.3 Departamentul</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px", textAlign: "left" }}>
                {subject?.department ? subject.department.nameRo : "Facultate"}
              </Col>
            </Row>
            {/* <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.3 Departamentul</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <Form.Item name="departmentId"  >
                <Select
                  disabled
                  className={styles.antSelect}
                  style={{ width: "100%", textAlign: "left" }}
                  placeholder={"Department"}>
                      {departments?.map((department) => (
                        <Option key={department.id} value={department.id}>
                            {department.departmentNameRo}
                        </Option>
                      ))}
                  </Select>
              </Form.Item>
              </Col>
            </Row> */}
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.4 Domeniul de studii</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{program?.domainName}</span>
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.5 Programul de studii universitare</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{program?.name}</span>
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.6 Ciclul de studii</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{ studyCycle === StudyCycleEnum.Bachelor ? "Licență" : "Master"}</span>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.7 Limba de predare</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{mapStudyProgramLangToString(program?.language ?? StudyProgramLangEnum.Romana)}</span>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>1.8 Locația geografică de desfășurare a studiilor</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{ program?.universityName === "CUB" ? "București" : "Pitești"}</span>
              </Col>
            </Row>

          </div>


          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex', marginTop: "15px" }}>2. Date despre disciplină</span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            
            <Row gutter={16}   style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.1 Denumirea disciplinei (ro)(en)</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="nameRO"
              >
                <Input disabled placeholder="Numele disciplinei RO" />
                
              </Form.Item>

              <Form.Item
                name="nameEN"
              >
                <Input disabled placeholder="Numele disciplinei EN" />
              </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.2 Titularul activităților de curs</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="courseCoordinatorIds"
                extra={coordinatorsHint}
              >
                <Select
                            className={styles.antSelect}
                            disabled={coordinatorsReadOnly}
                            mode="multiple"
                            onSelect={() => setLabUserSearchTerm('')}
                            maxTagCount={10}
                            allowClear
                            showSearch
                            style={{ width: "100%", textAlign: "left" }}
                            placeholder={"Căutare titular curs"}
                            filterOption={false}
                            onSearch={setCourseUserSearchTerm}
                            notFoundContent={
                              isLoadingCourseSearch? <Spin size="small" /> : null
                            }
                        >
                            {[...(subject?.courseCoordinators || []),
                              ...courseUsers?.data?.filter(user => !(subject?.courseCoordinators || []).find(u => u.id === user.id)) || []]
                                ?.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name} - {user.email}
                                    </Option>
                                ))
                                }
                        </Select>
              </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.3 Titularul activităților de seminar/ laborator/ proiect</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="labCoordinatorIds"
                extra={coordinatorsHint}
              >
                <Select
                            className={styles.antSelect}
                            disabled={coordinatorsReadOnly}
                            mode="multiple"
                            onSelect={() => setLabUserSearchTerm('')}
                            maxTagCount={10}
                            allowClear
                            showSearch
                            style={{ width: "100%", textAlign: "left" }}
                            placeholder={"Căutare titular laborator"}
                            filterOption={false}
                            onSearch={setLabUserSearchTerm}
                            notFoundContent={
                                isLoadingLabSearch ? <Spin size="small" /> : null
                            }
                        >
                            {[...(subject?.labCoordinators || []),
                              ...labUsers?.data?.filter(user => !(subject?.labCoordinators || []).find(u => u.id === user.id)) || []]
                                ?.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name} - {user.email}
                                    </Option>
                                ))
                                }
                        </Select>
              </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={3} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.4 Anul de studiu</span>
              </Col>
              <Col span={3} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="studyYear"
              >
                <Select disabled style={{ width: "100%" }} placeholder="Selectați anul de studii">
                  {studyCycle === "Bachelor" && (
                    <>
                      <Option value="L1">L1</Option>
                      <Option value="L2">L2</Option>
                      <Option value="L3">L3</Option>
                      <Option value="L4">L4</Option>
                    </>
                  )}
                  
                  {studyCycle === "Master" && (
                    <>
                      <Option value="M1">M1</Option>
                      <Option value="M2">M2</Option>
                    </>
                  )} 
                  {studyCycle === "Doctorate" && (
                    <>
                      <Option value="D1">D1</Option>
                    </>
                  )}
                </Select>
              </Form.Item>
              </Col>

              <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.5 Semestrul</span>
              </Col>
              <Col span={3} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="semester"
              >
                <Select disabled style={{width: "100%"}} placeholder="Selectați semestrul">
                  <Option value="S1">Semestrul 1</Option>
                  <Option value="S2">Semestrul 2</Option>
                </Select>
              </Form.Item>
              </Col>

              <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.6. Forma de evaluare</span>
              </Col>
              <Col span={3} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="evaluationType"
              >
                <Select disabled style={{width: "100%"}} placeholder="Selectați forma de evaluare">
                  <Option value="E">Examen</Option>
                  <Option value="V">Verificare</Option>
                </Select>
              </Form.Item>
              </Col>
              
              <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.7 Tipul/ Regimul disciplinei</span>
              </Col>
              <Col span={3} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="subjectCategory"
              >
                <Select disabled style={{ width: "100%" }} placeholder="Selectați categoria formativă">
                  <Option value="Mandatory">Obligatorie</Option>
                  <Option value="Optional">Opțională</Option>
                  <Option value="Facultative">Facultativă</Option>
                </Select>
              </Form.Item>
             
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={6} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.8 Categoria formativă</span>
              </Col>
              <Col span={3} style={{ padding: "8px 16px" }}>
               <Form.Item
                name="subjectType"
              >
                <Select disabled style={{ width: "100%" }} placeholder="Selectați tipul/ regimul disciplinei">
                  <Option value="Fundamental">Fundamental</Option>
                  <Option value="Specialty">Specializare</Option>
                  <Option value="Complementary">Complementară</Option>
                </Select>
              </Form.Item>
              </Col>

              <Col span={6} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Categorie disciplină</span>
              </Col>
              <Col span={3} style={{ padding: "8px 16px" }}>
                <Form.Item
                  name="disciplineCategory"
                  initialValue={isDoctorate ? DisciplineCategoryEnum.PracticeStage : DisciplineCategoryEnum.Standard}
                  rules={[{ required: true, message: "Selectați categoria disciplinei!" }]}
                >
                  <Select style={{ width: "100%" }} disabled placeholder="Selectați categoria disciplinei">
                    <Option value="Standard">Standard</Option>
                    <Option value="PracticeStage">Practică</Option>
                    <Option value="DiplomaProject">Elaborare proiect diplomă</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>2.10 Tipul de notare</span>
              </Col>
              <Col span={3} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="gradingType"
              >
                <Select disabled style={{width: "100%"}} placeholder="Selectați tipul de notare">
                  <Option value="Grade">Nota</Option>
                  <Option value="AdmRej">Admis/Respins</Option>
                </Select>
              </Form.Item>
              </Col>
            </Row>
          </div>

          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex', marginTop: "15px" }}>3. Timpul total estimat (ore pe semestru al activităților didactice)</span></Col></Row>
            <div style={{ border: "1px solid lightgrey", borderRadius: "5px", overflow: "hidden" }}>
              {category === DisciplineCategoryEnum.PracticeStage ? (
                <>
                  {/* S’ — doar un câmp pentru total ore practică */}
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>
                        Număr total de ore (practică)
                      </span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item
                        name="practiceTotalHours"
                        initialValue={0}
                        rules={[{ required: true, pattern: /^\d+(\.\d{1,2})?$/, message: "Introduceți numărul total de ore" }]}
                      >
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled={disabledByStatus} />
                      </Form.Item>
                    </Col>
                  </Row>

                  {!isDualProgramPlan &&  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Numărul de credite</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="credits">
                        <Input disabled={isAdmin}/>
                      </Form.Item>
                    </Col>
                  </Row>}
                  
                  {isDualProgramPlan && <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Numărul de ECTS (IIS)</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="creditsIIS">
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled />
                      </Form.Item>
                    </Col>
                  </Row>}
                  {isDualProgramPlan && 
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>3.10 Numărul de ECTS (OE)</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="creditsOE">
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled />
                      </Form.Item>
                    </Col>
                  </Row>}
                  {isDualProgramPlan && (
                    <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                      <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                        <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Practica desfășurată la operatorul economic</span>
                      </Col>
                      <Col span={4} style={{ padding: "8px 16px" }}>
                        <Form.Item name="practiceAtPartner" valuePropName="checked">
                          <Checkbox disabled={disabledByStatus}>Da</Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </>
              ) : (
                <>
                  {/* Varianta clasică pentru Standard și S’’ */}
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={4} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>3.1 Număr de ore pe săptămână</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="totalWeeklyHours"><Input disabled={true}/></Form.Item>
                    </Col>

                    <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Din care: 3.2 curs</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="courseWeeklyHours"><Input disabled={true}/></Form.Item>
                    </Col>

                    <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>
                        3.3 laborator<br/><br/><br/>{isDualProgramPlan && <><br/><br/></>}
                        seminar<br/><br/><br/>{isDualProgramPlan && <><br/><br/></>}
                        proiect
                        </span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="labWeeklyHours" 
                        rules={[{ required: true, pattern: /^\d+(\.\d{1,2})?$/, message: "Introduceți numărul de ore de laborator" }]}
                      >
                              <InputNumber min={0} precision={1} style={{ width: "100%" }} disabled={disabledByStatus} /> 
                      </Form.Item>
                      {isDualProgramPlan && (
                        <Form.Item name="labAtPartner" valuePropName="checked">
                          <Checkbox disabled={disabledByStatus}>Laborator la OE</Checkbox>
                        </Form.Item>
                      )}
                      <Form.Item name="seminarWeeklyHours" 
                        rules={[{ required: true, pattern: /^\d+(\.\d{1,2})?$/, message: "Introduceți numărul de ore de seminar" }]}
                      >
                        <InputNumber min={0} precision={1} style={{ width: "100%" }} disabled={disabledByStatus} /> 
                      </Form.Item>
                      {isDualProgramPlan && (
                        <Form.Item name="seminarAtPartner" valuePropName="checked">
                          <Checkbox disabled={disabledByStatus}>Seminar la OE</Checkbox>
                        </Form.Item>
                      )}
                      <Form.Item name="projectWeeklyHours" 
                        rules={[{ required: true, pattern: /^\d+(\.\d{1,2})?$/, message: "Introduceți numărul de ore de proiect" }]}
                      >
                        <InputNumber min={0} precision={1} style={{ width: "100%" }} disabled={disabledByStatus} /> 
                      </Form.Item>
                      {isDualProgramPlan && (
                        <Form.Item name="projectAtPartner" valuePropName="checked">
                          <Checkbox disabled={disabledByStatus}>Proiect la OE</Checkbox>
                        </Form.Item>
                      )}
                    </Col>
                  </Row>

                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={4} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>3.4 Total ore activități asistate</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="totalHours"><Input disabled={true}/></Form.Item>
                    </Col>

                    <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Din care: 3.5 curs</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="courseHours"><Input disabled={true}/></Form.Item>
                    </Col>

                    <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>3.6 laborator/ seminar/ proiect</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="labHours"><Input disabled={true}/></Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Distribuția fondului de timp:</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}><span>ore</span></Col>
                  </Row>
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{alignItems: 'left', height: '100%' }}>
                        <p style={{ display: 'flex', margin: '0'}}>Studiul după manual, suport de curs, bibliografie și notițe;</p>
                        <p style={{ display: 'flex', margin: '0'}}>Documentare suplimentară în bibliotecă, pe platformele electronice de specialitate;</p>
                        <p style={{ display: 'flex', margin: '0'}}>Pregătire seminarii/ laboratoare/ proiecte, teme, referate, portofolii și eseuri</p>
                      </span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="selfStudyHours" 
                        rules={[{ required: true, pattern: /^\d+$/, message: "Introduceți numărul de ore de studiu individual" }]}
                      >
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled={disabledByStatus} /></Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Tutorat</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="tutoringHours"
                      rules={[{ required: true, pattern: /^\d+$/, message: "Introduceți numărul de ore de tutorat" }]}
                      >
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled={disabledByStatus} /></Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Alte activități (dacă există):</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="otherActivitiesHours"
                      rules={[{ required: true, pattern: /^\d+$/, message: "Introduceți numărul de ore pentru alte activități" }]}
                      >
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled={disabledByStatus} /></Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>3.7 Total ore studiu individual</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="totalSelfStudyHours"><Input disabled={true}/></Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>3.8 Total ore pe semestru</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="totalSemesterHours"><Input disabled={true}/></Form.Item>
                    </Col>
                  </Row>
                  {!isDualProgramPlan && <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Numărul de credite</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="credits"><Input disabled={true}/></Form.Item>
                    </Col>
                  </Row>}
                  {isDualProgramPlan && 
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Numărul de credite (IIS)</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="creditsIIS">
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled />
                      </Form.Item>
                    </Col>
                  </Row>}
                  {isDualProgramPlan && 
                  <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                    <Col span={20} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                      <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>3.10 Numărul de credite (OE)</span>
                    </Col>
                    <Col span={4} style={{ padding: "8px 16px" }}>
                      <Form.Item name="creditsOE">
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled />
                      </Form.Item>
                    </Col>
                  </Row>}
                </>
              )}
            </div>
          
          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex', marginTop: "15px" }}>4. Precondiții (acolo unde este cazul)</span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={4} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <span style={{ marginRight: '5px' }}>4.1 de curriculum</span>
                  <Tooltip
                    overlayStyle={{ maxWidth: '800px' }}
                    placement="right"
                    title={
                      <>
                        <p>Exemplu:</p>
                        <p>Parcurgerea și/sau promovarea următoarelor discipline:</p>
                        <ul style={{ paddingLeft: '1em', margin: 0 }}>
                          <li>Abc</li>
                        </ul>
                      </>
                    }
                  >
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </span>
              </Col>
              <Col span={20} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="curriculumPrecondition"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(curriculumPrecondition: string) => {
                                form.setFieldsValue({
                                  ...form.getFieldsValue(),
                                    curriculumPrecondition,
                                } as any);
                            }}
                        />
              </Form.Item>         
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={4} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <span style={{ marginRight: '5px' }}>4.2 de rezultate ale învățării</span>
                  <Tooltip
                    overlayStyle={{ maxWidth: '800px' }}
                    placement="right"
                    title={
                      <>
                        <p>Exemplu:</p>
                        <p>Acumularea următoarelor cunoștințe:</p>
                        <ul style={{ paddingLeft: '1em', margin: 0 }}>
                          <li>Abc</li>
                        </ul>
                      </>
                    }
                  >
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </span>
              </Col>
              <Col span={20} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="respultsPrecondition"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(respultsPrecondition: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    respultsPrecondition,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>
          </div>

          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex', marginTop: "15px" }}>5. Condiții necesare pentru desfășurarea optimă a activităților didactice (acolo unde este cazul)</span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={4} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <span style={{ marginRight: '5px' }}>5.1 Curs</span>
                  <Tooltip
                    overlayStyle={{ maxWidth: '800px' }}
                    placement="right"
                    title={
                      <>
                        <p>Exemplu:</p>
                        <ul style={{ paddingLeft: '1em', margin: 0 }}>
                          <li>Cursul se va desfășura într-o sală dotată cu videoproiector și computer.</li>
                          <li>Abc</li>
                        </ul>
                      </>
                    }
                  >
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </span>
              </Col>
              <Col span={20} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="courseConditions"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(courseConditions: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    courseConditions,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={4} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <span style={{ marginRight: '5px' }}>5.2 Seminar/ Laborator/ Proiect</span>
                  <Tooltip
                    overlayStyle={{ maxWidth: '800px' }}
                    placement="right"
                    title={
                      <>
                        <p>Exemplu:</p>
                        <ul style={{ paddingLeft: '1em', margin: 0 }}>
                          <li>Laboratorul se va desfășura într-o sală cu dotare specifică, care trebuie să includă: abc</li>
                          <li>Pentru desfășurarea activităților de laborator sunt necesari următorii reactivi: abc</li>
                        </ul>
                      </>
                    }
                  >
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </span>
              </Col>
              <Col span={20} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="labConditions"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(labConditions: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    labConditions,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>
          </div>

          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', marginTop: '15px', alignItems: 'center' }}>
                          <b style={{ marginRight: '5px' }}>6. Obiectiv general</b>
                          <Tooltip
                            overlayStyle={{ maxWidth: '800px' }}
                            placement="right"
                            title={
                              <>
                                <p>Se referă la intențiile cadrelor didactice pentru studenți, la ceea ce studenții vor fi învățați în timpul disciplinei.</p>
                                <p>
                                  Oferă o orientare cu privire la locul disciplinei în cadrul domeniului științific abordat, precum și la rolul pe care acesta îl are în cadrul programului de studii universitare.
                                  Vor fi descrise de o manieră generală tematicile abordate, justificarea includerii cursului în planul de învățământ al programului de studii universitare etc.
                                </p>
                                <p><b>Exemplu:</b></p>
                                <p>
                                  Această disciplină se studiază în cadrul domeniului abc /specializării abc și își propune să familiarizeze studenții cu principalele abordări, modele și teorii explicative ale domeniului, utilizate în rezolvarea de aplicații practice și probleme, cu relevanță pentru stimularea procesului de învățare la studenți.
                                </p>
                                <p>
                                  Disciplina abordează ca tematică specifică următoarele noțiuni de bază/avansate, concepte și principii specifice, toate acestea contribuind la transmiterea/formarea către/la studenți a unei viziuni de ansamblu asupra reperelor metodologice și procedurale aferente domeniului.
                                </p>
                              </>
                            }
                          >
                            <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                          </Tooltip>
                        </span>
            </Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="generalObjective"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(generalObjective: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    generalObjective,
                                } as any);
                            }}
                        />
              </Form.Item>          
              </Col>
            </Row>

            
          </div>

          <Row gutter={16}>
            <Col span={24}>
              <span style={{
                fontSize: '16px',
                display: 'flex',
                justifyContent: "flex-start",
                flexWrap: "wrap",
                marginTop: "15px",
                alignItems: 'center'
              }}>
                <b style={{ marginRight: '5px' }}>7. Rezultatele învățării</b>
                <Tooltip
                  overlayStyle={{ maxWidth: '800px' }}
                  placement="right"
                  title={
                    <>
                      <p>Sunt enunțuri sintetice referitoare la ceea ce un student va fi capabil să facă sau să demonstreze la finalizarea unui curs. Rezultatele învățării reflectă realizările studentului și mai puțin intențiile profesorului. Rezultatele învățării informează studenții despre ceea ce se așteaptă de la ei din punct de vedere al performanței, pentru a obține notele și creditele dorite. Sunt definite în termeni concreți, folosind verbe similare exemplelor de mai jos și indică ceea ce se va urmări prin evaluare.</p>
                      <ul style={{ paddingLeft: 16 }}>
                        <li>Rezultatele învățării să fie utile, fără descrieri prea detaliate care produc confuzie, dar nici generale, deoarece informațiile pot deveni lipsite de sens.</li>
                        <li>Rezultatele învățării trebuie corelate cu rezultatele generale ale programului.</li>
                        <li>Rezultatele învățării trebuie să fie observabile și măsurabile.</li>
                        <li>Rezultatele învățării trebuie să fie convingătoare, clare, utilizabile în orice formă de evaluare (formală, non-formală sau informală).</li>
                        <li>Rezultatele învățării se îmbunătățesc permanent prin feedback de la studenți și piață.</li>
                      </ul>
                    </>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                </Tooltip>
              </span>
            </Col>
          </Row>

          <div style={{
            border: "1px solid lightgrey",
            borderRadius: "5px",
            padding: "8px 16px",
          }}>
          <Typography.Paragraph style={{ fontWeight: 600, marginTop: '10px' }}>
            Rezultate noi adăugate: {addedOutcomesCount} (necesar {MIN_ADDED_OUTCOMES}-{MAX_ADDED_OUTCOMES})
          </Typography.Paragraph>

          <Row gutter={16}><Col span={24}>
            <div style={{ textAlign: "left", marginTop: "10px" }}>
              a. Cunoștințe{' '}
              <Tooltip
                overlayStyle={{ maxWidth: '800px' }}
                placement="right"
                title={
                  <>
                    <p><b>Rezultatul asimilării de informații prin învățare.</b> Cunoștințele reprezintă ansamblul de fapte, principii, teorii și practici legate de un anumit domeniu. Pot fi teoretice și/sau faptice.</p>
                    <p><b>Verbe – memorie/reamintire:</b> Recunoaște, identifică, menționează, redă, enumeră</p>
                    <p><b>Verbe – înțelegere:</b> Redă în cuvinte proprii, explică, exemplifică, clasifică, compară</p>
                    <p><b>Exemple:</b></p>
                    <ul style={{ paddingLeft: 16 }}>
                      <li>Enumeră etapele dezvoltării domeniului</li>
                      <li>Explică noțiuni specifice domeniului</li>
                      <li>Recunoaște procese/fenomene/structuri</li>
                      <li>Răspunde la întrebări</li>
                      <li>Compară noțiuni</li>
                    </ul>
                  </>
                }
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
              </Tooltip>
            </div>
          </Col></Row>
          <Row gutter={16}><Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
            <ProgramCompetenceForm
              name="fisaKnowledgeOutcomeValues"
              initialValue={[]}
              rules={false}
              disabled={disabledByStatus}
              allowDeleteLastItem
              identifierPrefix="C"
              buttonText="Adaugă rezultat"
              onSelectionInvalidated={handleOutcomeSelectionInvalidated}
            />
          </Col></Row>

          <Row gutter={16}><Col span={24}>
            <div style={{ textAlign: "left", marginTop: "10px" }}>
              b. Abilități{' '}
              <Tooltip
                overlayStyle={{ maxWidth: '800px' }}
                placement="right"
                title={
                  <>
                    <p><b>Capacitatea de a aplica cunoștințe și de a utiliza know-how</b> pentru a rezolva sarcini și probleme.</p>
                    <p><b>Tipuri:</b> Cognitive (gândire logică, intuitivă, creativă) și practice (abilități manuale, utilizarea de instrumente etc.)</p>
                    <p><b>Verbe – aplicare:</b> Identifică soluția, aplică teoria, dezvoltă, produce</p>
                    <p><b>Verbe – sinteză:</b> Formulează puncte de vedere, creează, concepe, adaptează</p>
                    <p><b>Exemple:</b></p>
                    <ul style={{ paddingLeft: 16 }}>
                      <li>Utilizează principii specifice</li>
                      <li>Formulează concluzii și puncte de vedere</li>
                      <li>Creează un text științific</li>
                      <li>Identifică soluții</li>
                      <li>Anticipează etapele rezolvării</li>
                    </ul>
                  </>
                }
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
              </Tooltip>
            </div>
          </Col></Row>
          <Row gutter={16}><Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
            <ProgramCompetenceForm
              name="fisaSkillOutcomeValues"
              initialValue={[]}
              rules={false}
              disabled={disabledByStatus}
              allowDeleteLastItem
              identifierPrefix="A"
              buttonText="Adaugă rezultat"
              onSelectionInvalidated={handleOutcomeSelectionInvalidated}
            />
          </Col></Row>

          <Row gutter={16}><Col span={24}>
            <div style={{ textAlign: "left", marginTop: "10px" }}>
              c. Responsabilitate și autonomie{' '}
              <Tooltip
                overlayStyle={{ maxWidth: '800px' }}
                placement="right"
                title={
                  <>
                    <p><b>Capacitatea de a acționa în mod autonom și responsabil folosind cunoștințele și abilitățile dobândite.</b></p>
                    <p><b>Verbe – analiză:</b> Compară, diferențiază, formulează concluzii, identifică, distinge</p>
                    <p><b>Verbe – evaluare:</b> Verifică, argumentează, stabilește criterii, estimează, validează, interpretează</p>
                    <p><b>Exemple:</b></p>
                    <ul style={{ paddingLeft: 16 }}>
                      <li>Respectă principiile eticii academice</li>
                      <li>Demonstrează receptivitate pentru contexte noi</li>
                      <li>Manifestă colaborare și implicare socială</li>
                      <li>Conștientizează valoarea contribuției proprii</li>
                      <li>Aplică principii de etică profesională</li>
                      <li>Analizează oportunități de afaceri</li>
                      <li>Gestionează situații din viața reală</li>
                    </ul>
                  </>
                }
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
              </Tooltip>
            </div>
          </Col></Row>
          <Row gutter={16}><Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
            <ProgramCompetenceForm
              name="fisaResponsibilityOutcomeValues"
              initialValue={[]}
              rules={false}
              disabled={disabledByStatus}
              allowDeleteLastItem
              identifierPrefix="RA"
              buttonText="Adaugă rezultat"
              onSelectionInvalidated={handleOutcomeSelectionInvalidated}
            />
          </Col></Row>
          </div>

          {/*
            These three fields no longer have a visible editor (replaced by
            the ProgramCompetenceForm-based editor above), but legacy fișe may still carry historical
            free-text values in them. They're still populated via form.setFieldsValue(...)
            in the getSubject onSuccess handler and still sent in the save payloads
            (handleSave / handlePartialSave). Keeping them as hidden-but-registered
            Form.Item entries ensures form.validateFields() (used by handleSave) returns
            their real values instead of undefined — rc-field-form only includes fields
            that have a mounted Form.Item in its returned value set. Without this, a full
            save (not partial save) would send undefined for these fields and null out
            existing DB history. Do not remove unless these columns are deprecated
            server-side too.
          */}
          <Form.Item name="teachingKnowledges" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="teachingAptitudes" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="teachingResponsabilities" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Row gutter={16}><Col span={24}>
            <span style={{ fontSize: '16px', display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', marginTop: '15px', alignItems: 'center' }}>
              <b style={{ marginRight: '5px' }}>8. Metode de predare</b>
              
              <Tooltip
                overlayStyle={{ maxWidth: '800px' }}
                placement="right"
                title={
                  <>
                    <p>
                      Se vor avea în vedere metode care să asigure predarea centrată pe student. 
                      Se va descrie modul în care se asigură participarea studenților la stabilirea propriului parcurs de învățare, 
                      cum se identifică eventualele rămâneri în urmă și ce măsuri remediale se adoptă în astfel de cazuri.
                    </p>
                    <p><b>Exemplu:</b></p>
                    <p>
                      Pornindu-se de la analiza caracteristicilor de învățare ale studenților și de la nevoile lor specifice, procesul de predare va explora metode de predare atât expozitive (prelegerea, expunerea), cât și conversative-interactive, bazate pe modele de învățare prin descoperire facilitate de explorarea directă și indirectă a realității (experimentul, demonstrația, modelarea), dar și pe metode bazate pe acțiune, precum exercițiul, activitățile practice și rezolvarea de probleme.
                    </p>
                    <p>
                      În activitatea de predare vor fi utilizate prelegeri, în baza unor prezentări PowerPoint sau diferite filmulețe care vor fi puse la dispoziția studenților. 
                      Fiecare curs va debuta cu recapitularea capitolelor deja parcurse, cu accent asupra noțiunilor parcurse la ultimul curs.
                    </p>
                    <p>
                      Prezentările utilizează imagini și scheme, astfel încât informațiile prezentate să fie ușor de înțeles și asimilat.
                    </p>
                    <p>
                      Această disciplină acoperă informații și activități practice menite să-i sprijine pe studenți în eforturile de învățare și de dezvoltare a unor relații optime de colaborare și comunicare într-un climat favorabil învățării prin descoperire.
                    </p>
                    <p>
                      Se va avea în vedere exersarea abilităților de ascultare activă și de comunicare asertivă, precum și a mecanismelor de construcție a feedback-ului, ca modalități de reglare comportamentală în situații diverse și de adaptare a demersului pedagogic la nevoile de învățare ale studenților.
                    </p>
                    <p>
                      Se va exersa abilitatea de lucru în echipă pentru rezolvarea diferitelor sarcini de învățare.
                    </p>
                    <p>Abc</p>
                  </>
                }
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
              </Tooltip>
            </span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="teachingMethods"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(teachingMethods: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    teachingMethods,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>
          </div>
          
          <Row gutter={16}><Col span={24}>
            <span style={{ fontSize: '16px', display: 'flex', justifyContent: "flex-start", flexWrap: "wrap", marginTop: "15px" }}><b>9. Conținuturi</b></span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><b>CURS</b></Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              
              <SubjectContentForm disabled={disabledByStatus}
                            name="courseContents"
                            initialValue={[{
                              nrCrt: 1,
                              content: "",
                              hoursNumber: 0
                            }]}
                            rules={true}
                        
                        ></SubjectContentForm>
              
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><span style={{ fontSize: '16px', display: 'flex', alignItems: 'center', marginTop: '15px' }}>
                <b style={{ marginRight: '5px' }}>Bibliografie:</b>
                <Tooltip
                  overlayStyle={{ maxWidth: '800px' }}
                  placement="right"
                  title={
                    <ol style={{ paddingLeft: '1.5em', margin: 0 }}>
                      <li>Pe prima poziție se trece cursul în format electronic din Moodle.  
                      Nume și prenume titular de disciplină, denumirea disciplinei, suport de curs electronic, link-ul cursului din Moodle</li>
                      <li>Cel puțin 50% reprezintă titluri de carte sau cursuri de specialitate apărute în ultimii 10 ani în edituri recunoscute (conform indicatorilor de performanță din Raportul de evaluare al comisiei de experți evaluatori ARACIS).</li>
                      <li>Toate titlurile trebuie să fie accesibile pentru studenți, în biblioteca universității / facultății / departamentului, în baze de date online etc.</li>
                    </ol>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                </Tooltip>
              </span></Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="courseBibliography"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(courseBibliography: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    courseBibliography,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><b>LABORATOR/ SEMINAR/ PROIECT</b></Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              
              <SubjectContentForm disabled={disabledByStatus}
                            name="labContents"
                            initialValue={[{
                              nrCrt: 1,
                              content: "",
                              hoursNumber: 0
                            }]}
                            rules={true}
                        
                        ></SubjectContentForm>
              
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><span style={{ fontSize: '16px', display: 'flex', alignItems: 'center', marginTop: '15px' }}>
                <b style={{ marginRight: '5px' }}>Bibliografie:</b>
                <Tooltip
                  overlayStyle={{ maxWidth: '800px' }}
                  placement="right"
                  title={
                    <ol style={{ paddingLeft: '1.5em', margin: 0 }}>
                      <li>Pe prima poziție se trece cursul în format electronic din Moodle.  
                      Nume și prenume titular de disciplină, denumirea disciplinei, suport de curs electronic, link-ul cursului din Moodle</li>
                      <li>Cel puțin 50% reprezintă titluri de carte sau cursuri de specialitate apărute în ultimii 10 ani în edituri recunoscute (conform indicatorilor de performanță din Raportul de evaluare al comisiei de experți evaluatori ARACIS).</li>
                      <li>Toate titlurile trebuie să fie accesibile pentru studenți, în biblioteca universității / facultății / departamentului, în baze de date online etc.</li>
                    </ol>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                </Tooltip>
              </span></Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="labBibliography"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(labBibliography: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    labBibliography,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>
          </div>

          <Row gutter={16}><Col span={24}>
            <span style={{ fontSize: '16px', display: 'flex', alignItems: 'center', marginTop: '15px' }}>
              <b style={{ marginRight: '5px' }}>10. Evaluare</b>
              <Tooltip
                overlayStyle={{ maxWidth: '800px' }}
                placement="right"
                title={
                  <p>
                    În cazul verificărilor pe parcurs cu degrevare, fișa disciplinei trebuie să conțină în mod clar câte evaluări de acest fel sunt în timpul unui semestru, 
                    în care săptămâni și ce capitole cuprinde și degrevează fiecare verificare, respectiv ce pondere din nota finală are fiecare verificare cu degrevare 
                    (cf. Art. 11, alin. (7) din Regulamentul privind organizarea și funcționarea studiilor universitare de licență din POLITEHNICA București)
                  </p>
                }
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
              </Tooltip>
            </span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><b>CURS</b></Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>             
              <SubjectEvaluationForm disabled={disabledByStatus}
                            name="courseEvaluations"
                            initialValue={[{
                              nrCrt: 1,
                              content: "",
                              hoursNumber: 0
                            }]}
                            rules={true}
                        
                        ></SubjectEvaluationForm>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><b>Seminar/ laborator/ proiect</b></Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="category"
              >
              <SubjectEvaluationForm disabled={disabledByStatus}
                            name="labEvaluations"
                            initialValue={[{
                              nrCrt: 1,
                              content: "",
                              hoursNumber: 0
                            }]}
                            rules={true}
                        
                        ></SubjectEvaluationForm>
              </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><span style={{ fontSize: '16px', display: 'flex', alignItems: 'center', marginTop: '15px' }}>
                <b style={{ marginRight: '5px' }}>10.6 Condiții de promovare</b>
                <Tooltip
                  overlayStyle={{ maxWidth: '800px' }}
                  placement="right"
                  title={
                    <>
                      <p><b>Exemplu:</b></p>
                      <ul style={{ paddingLeft: '1em', margin: 0 }}>
                        <li>Obținerea a 50% din punctajul total.</li>
                        <li>Obținerea a 50% din punctajul aferent activității pe parcursul semestrului.</li>
                      </ul>
                      <p style={{ color: 'red', marginTop: '5px' }}>
                        Atenție la Regulamentul de studii aplicabil, se pot include aici referințe în acest sens!
                      </p>
                    </>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                </Tooltip>
              </span></Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="promotionConditions"
              >
              <CustomEditor disabled={disabledByStatus}
                            onEditorChange={(promotionConditions: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    promotionConditions,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", textAlign: "left" }}>
              <Col span={24} style={{ padding: "8px 16px" }}><span style={{ fontSize: '16px', display: 'flex', alignItems: 'center', marginTop: '15px' }}>
                <b style={{ marginRight: '5px' }}>10.7 Rezultatele învățării minimale pentru promovarea disciplinei</b>
                <Tooltip
                  overlayStyle={{ maxWidth: '800px' }}
                  placement="right"
                  title={
                    <p>Selectați, dintre rezultatele învățării de la secțiunea 7 (proprii sau corelate din program), pe cele considerate minimale pentru promovarea disciplinei. Vor apărea ca listă pe fișa disciplinei.</p>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                </Tooltip>
              </span></Col>
            </Row>
            <Row gutter={16}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="minimumPromotionOutcomeIds"
              >
                <Select
                  mode="multiple"
                  disabled={disabledByStatus}
                  allowClear
                  style={{ width: "100%", textAlign: "left" }}
                  placeholder="Alege rezultatele minimale pentru promovare"
                >
                  {minimumOutcomeOptions.map(option => (
                    <Option key={option.key} value={option.id ?? option.key} disabled={!option.id}>
                      {option.label}{!option.id && ' (salvați fișa pentru a o putea selecta)'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              </Col>
            </Row>
          </div>

          {/* <Row gutter={16}><Col span={24}>
            <span style={{ fontSize: '16px', display: 'flex', justifyContent: "flex-start", flexWrap: "wrap", marginTop: "15px" }}><b>12. Coroborarea conținutului disciplinei cu așteptările reprezentanților angajatorilor și asociațiilor profesionale reprezentative din domeniul aferent programului, precum și cu stadiul actual al cunoașterii în domeniul științific abordat și practicile în instituții de învățământ superior din Spațiul European al Învățământului Superior (SEÎS)</b></span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={24} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="subjectCoroboration"
              >
              <CustomEditor disabled={disabledByStatus} 
                            onEditorChange={(subjectCoroboration: string) => {
                                form.setFieldsValue({
                                    ...form.getFieldsValue(),
                                    subjectCoroboration,
                                } as any);
                            }}
                        />
              </Form.Item>
              </Col>
            </Row>
          </div> */}
          <div className={styles.secondLine} style={{ marginTop: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div className={styles.firstColumn} style={{ display: "flex", width: "250px", justifyContent: "space-between " }}>
                <ConfirmationModal
                  nameButton={t('internships.addInternshipForm.cancel')}
                  modalText={t('internships.cancelMessage')}
                  titleButton={''}
                  handleFunction={handleCancel}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.green, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
                <ConfirmationModal
                  nameButton={subjectId ? t('internships.addInternshipForm.updateButton') : t('internships.addInternshipForm.saveButton')}
                  modalText={t('internships.saveMessage')}
                  titleButton={''}
                  handleFunction={handleSave}
                  closeOnConfirm={true}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.secondColor, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
              </div>

              {((subject?.status ?? SubjectStatusEnum.Draft) === SubjectStatusEnum.Draft) && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <ConfirmationModal
                    nameButton={subjectId ? "Actualizează parțial" : "Salvează parțial"}
                    modalText={"Salvați parțial fișa (fără validări)?"}
                    titleButton={''}
                    handleFunction={handlePartialSave}
                    closeOnConfirm={true}
                    iconButton={null}
                    shapeButton={''}
                    typeButton={''}
                    disabledProp={false}
                    styleProp={{ background: theme.secondColor, color: theme.white, marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                  />
                </div>
              )}
            </div>                    
        </Form>
        <ImportSubjectModal
          visible={isImportModalVisible}
          onCancel={() => setIsImportModalVisible(false)}
          onConfirm={handleImportSubject}
          studyCycle={studyCycle}
        />
      </div>
    </Layout>
    );
  }
};

export default SubjectForm;